import React from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useAuthContext } from "shared/context/auth-context";
import { client } from "shared/helpers/client";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { appendFormData, localize, stringify } from "shared/helpers/utils";
import { useLookups } from "shared/hooks/use-lookups";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

interface CompanyInfoResponse {
  additionalNumber: number;
  apartmentNumber: number;
  buildingNumber: number;
  countryId:string;
  currencyId:string;
  neighborhoodName: string;
  postalCode: string;
  streetName: string;
  cityName: string;
  companyName: string;
  isPendingExist: boolean;
  vatNumber: string;
  commercialRegister:any;
  extensionNumber: string;
  companyLogo:File
}

interface CompanyInfoData {
  companyName: string;
  commercialRegister: string;
  cityName: string;
  countryId:string;
  currencyId:string;
  neighborhoodName: string;
  streetName: string;
  buildingNumber: number;
  apartmentNumber: number;
  postalCode: string;
  extensionNumber: any;
  vatNumber: string;
  companyLogo: File
}

export function useCompanyInfo() {
  const { t } = useTranslation();
  const [{ user }] = useAuthContext();
  const queryClient = useQueryClient();

  const schema = React.useMemo(() => {
    return yup.object().shape({
      companyName: yup.string().required(t("errors.required")),
      commercialRegister: yup.string().required(t("errors.required")),
      vatNumber: yup.string().required(t("errors.required")),
      countryId: yup.string().required(t("errors.required")).nullable(),
      currencyId: yup.string().required(t("errors.required")).nullable(),
    });
  }, [t]);
  const methods = useForm<CompanyInfoData>({
    resolver: yupResolver(schema),
    defaultValues: {
      cityName: '',
      countryId: '',
      currencyId: '',
      postalCode:'',
    },
  });

  const { mutate: callChangeCompanyInfoServer ,isLoading} = useMutation((companyData: any) =>
    client("/Owner/UpdateComapnyProfile", { data: companyData, headers: { "Content-Type": "multipart/form-data" }})
  );

  const { data: companyInfoData } = useQuery(
    ["company-info", user.sub],
    () =>
      client<CompanyInfoResponse>("/Owner/GetCompanyProfile").then((r) => r.response),
    {
      onSuccess(companyInfo) {
        methods.reset({
          companyName: companyInfo?.companyName ?? "",
          commercialRegister: companyInfo?.commercialRegister,
          vatNumber: companyInfo?.vatNumber,
          countryId:companyInfo?.countryId,
          currencyId:companyInfo?.currencyId,
          cityName: companyInfo?.cityName,
          neighborhoodName: companyInfo?.neighborhoodName,
          streetName: companyInfo?.streetName,
          buildingNumber: companyInfo?.buildingNumber,
          apartmentNumber: companyInfo?.apartmentNumber,
          postalCode: companyInfo?.postalCode,
          extensionNumber: companyInfo?.extensionNumber,
          companyLogo: companyInfo?.companyLogo
        });
      },
    }
  );


  const currencyId = methods.watch("currencyId");
  const { countries, currencies } = useLookups(["countries", "currencies"], currencyId);
  async function changeCompanyInfo(data: CompanyInfoData) {
    // TODO: Add Files
    const companyInfoDto = {
      companyName: data.companyName,
      commercialRegister:data.commercialRegister,
      vatNumber : data.vatNumber,
      cityName: data.cityName,
      countryId:data.countryId,
      currencyId:data.currencyId,
      neighborhoodName: data.neighborhoodName,
      streetName: data.streetName ?? '',
      buildingNumber: data.buildingNumber ?? '',
      apartmentNumber: data.apartmentNumber ?? '',
      postalCode: data.postalCode ?? '',
      extensionNumber: data.extensionNumber ?? '',
      companyLogo: data.companyLogo
    };
    const formData = appendFormData(companyInfoDto)

    callChangeCompanyInfoServer(formData, {
      onSuccess({ response, error }) {
        if (response) toast.success(t("form.save-success"));
       // if (error) toast.error(t("errors.server-error"));
      },
    });
  }

  return {
    ...methods,
    isLoading,
    companyInfoData,
    changeCompanyInfo,
    countries,
    currencies
  };
}
